/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

/**
 * Hook to unlock the submission when the window, browser is closed or component is unmounted
 */
import { matchPath, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { patchLockSubmissionThunk, patchUnlockSubmissionThunk } from './submission.thunk';
import { selectSubmissionLockedSubmissionId } from './submission.selectors';
import { RoutePath } from '../../../shared/enums/RoutePath';

/**
 * Submission locking/unlocking hook
 */
export function useSubmissionLock() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { path, shouldLockSubmission } = RoutePath.getRoutePath(location?.pathname);
  const submissionIdParam = matchPath(location?.pathname, path)?.params?.submissionId;
  const lockedSubmissionId = useSelector(selectSubmissionLockedSubmissionId);

  const onLock = () => {
    if (shouldLockSubmission /* && !lockedSubmissionId */ && submissionIdParam) {
      dispatch(patchLockSubmissionThunk({ submissionId: submissionIdParam }));
    }
  };

  const onUnlock = () => {
    if (!shouldLockSubmission && lockedSubmissionId) {
      dispatch(patchUnlockSubmissionThunk({ submissionId: lockedSubmissionId }));
    }
  };

  const onBeforeUnload = () => {
    if (lockedSubmissionId) {
      dispatch(patchUnlockSubmissionThunk({ submissionId: lockedSubmissionId }));
    }
  };

  // locking effect
  useEffect(() => {
    onLock();
  }, [submissionIdParam, onLock, location]);

  // unlocking effect
  useEffect(() => {
    onUnlock();
  }, [lockedSubmissionId, onUnlock, location]);

  // close tab/browser effect
  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [onBeforeUnload]);
}
